import Breadcrumbs from '@storeblocks/breadcrumbs';
import { Tab } from '@storeblocks/tabs';
import { type FC } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Permission } from '@/api/permissions/models/Permission';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { useCurrentUserInfo } from '@/auth/hooks';
import { CenteredPageContent } from '@/components/CenteredPageContent';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { PageHeading } from '@/components/PageHeading';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { ConnectTabs } from '@/components/Pretzel/ConnectTabs';
import { UserType } from '@/config/types';
import { useTexts } from '@/hooks/useTexts';

import { OrganizationAdmin } from './Organizations/OrganizationAdmin';
import { IndividualUserAdmin } from './Users/IndividualUserAdmin';
import { OrganizationUserAdmin } from './Users/OrganizationUserAdmin';

export const AdminPage: FC = () => {
  const organizationCmId = useOrganizationCmId();
  const { userType } = useCurrentUserInfo();
  const hasPermission = useHasPermission();

  const texts = useTexts();

  const { tabId } = useParams();

  const navigate = useNavigate();

  const handleChange = (_: unknown, newTabId: string): void => {
    navigate(`../admin/${newTabId}`, { replace: true });
  };

  if (!tabId) {
    return null;
  }

  return (
    <CenteredPageContent data-test="admin-page">
      <Breadcrumbs expandAllAriaLabel={texts.breadcrumbs.expandAllAriaLabel}>
        <InternalLink to={`/${organizationCmId}`}>
          {texts.menu.links.home}
        </InternalLink>
        <InternalLink to={`/${organizationCmId}/admin/${tabId}`}>
          {texts.menu.links.administration}
        </InternalLink>
      </Breadcrumbs>

      <MainContent>
        <PageHeading header={texts.menu.links.administration} />

        {userType === UserType.External && <OrganizationUserAdmin />}

        {userType === UserType.Internal &&
          hasPermission(Permission.CustomerAdminWrite) && (
            <div data-test="organization-admin">
              <ConnectTabs
                activeTabId={tabId}
                aria-label="Administration tabs"
                onChange={handleChange}
              >
                <Tab id="orgusers" title={texts.admin.users.organization.title}>
                  <OrganizationUserAdmin />
                </Tab>
                <Tab id="allusers" title={texts.admin.users.all.title}>
                  <IndividualUserAdmin />
                </Tab>
                <Tab
                  id="orgadmin"
                  title={texts.admin.organization.title}
                  data-test="admin-page-organizations"
                >
                  <OrganizationAdmin />
                </Tab>
              </ConnectTabs>
            </div>
          )}
      </MainContent>
    </CenteredPageContent>
  );
};
